
import React, { useEffect, ReactNode, Suspense, useRef, useState } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate, useParams } from 'react-router-dom';
import { db } from './services/db';
import { trackPageView } from './services/analytics';
import { setSentryUser, clearSentryUser, captureException as sentryCaptureException } from './services/sentry';
import { lazyComponent } from './utils/lazyComponent';
import OptimizedLandingPage from './components/landing/OptimizedLandingPage';
import RouteSeoPolicy from './components/RouteSeoPolicy';
import ThemeRoutePolicy from './components/ThemeRoutePolicy';
import { shouldUseRedesignForSurface, useUiReleaseMode } from './config/uiRelease';
import { isKnownHarmlessBrowserDomError } from './utils/errorClassifiers';

// Lazy-loaded auth and legal components to reduce initial bundle size
const Auth = React.lazy(() => import('./components/Auth'));
const Legal = React.lazy(() => import('./components/Legal'));
const VerifyEmail = React.lazy(() => import('./components/VerifyEmail'));
const ResetPassword = React.lazy(() => import('./components/ResetPassword'));
const SSOHandler = React.lazy(() => import('./components/SSOHandler'));

// Lazy-loaded core components
// ⚡ Routed through lazyComponent() (not bare React.lazy) so a stalled chunk
// fetch rejects after a timeout instead of hanging forever — and emits a Sentry
// event. This is the safety net for reaching /dashboard or /admin via any path
// other than the login screen (direct URL, in-app nav), where Auth's login
// hand-off watchdog does not apply.
const Dashboard = lazyComponent(() => import('./components/Dashboard'), 'Dashboard');
const AdminPanel = lazyComponent(() => import('./components/admin/AdminPanel'), 'AdminPanel');
const HelpDesk = React.lazy(() => import('./components/helpdesk/HelpDesk'));
const Blog = React.lazy(() => import('./components/blog/Blog'));
const ChatWidget = React.lazy(() => import('./components/ChatWidget'));
const PublicChatReply = React.lazy(() => import('./components/PublicChatReply'));
const AboutPage = React.lazy(() => import('./components/AboutPage'));
const AccountDeletionPage = React.lazy(() => import('./components/AccountDeletionPage'));

// Homepage — keep the primary landing page in the initial app bundle so the
// first paint does not flash the generic Suspense loader before the hero.
const LandingPage = React.lazy(() => import('./components/landing/LandingPage'));
const MetadeckLanding = React.lazy(() => import('./components/landing/MetadeckLanding'));
const YouTubeSmmPreview = React.lazy(() => import('./components/youtube_smm/YouTubeSmmPreview'));
const YouTubeLanding = React.lazy(() => import('./components/landing/YouTubeLanding'));
const PricingPage = React.lazy(() => import('./components/PricingPage'));
const LandingRedesignPage = React.lazy(() => import('./components/landing/LandingRedesignPreview'));
const AdminRedesignPage = React.lazy(() => import('./components/admin/redesign/AdminRedesignPreview'));
// Der V2-Entwurf ist am 09.08.2026 auf Wunsch entfernt worden (Aussehen).
// Gesichert auf dem Branch `archive/landing-v2-20260809` — dort liegen auch
// LandingCompare (Vorher/Nachher-Slider) und die Lieferprotokoll-Grafik.
// `LandingRedesignV2.css` ist absichtlich geblieben: LandingRedesignV3 importiert sie.
// V3: derselbe Inhalt und dasselbe Farbsystem wie V2, Praesentation mit React Bits
// (components/reactbits/). Ebenfalls nur lokal — zieht gsap/ogl nach und laedt
// deshalb bewusst lazy, damit nichts davon im Bundle der Live-Startseite landet.
const LandingRedesignV3Page = React.lazy(() => import('./components/landing/LandingRedesignV3'));
const PublicSpecialReleaseSurface = React.lazy(() => import('./components/landing/PublicSpecialReleaseSurface'));
const ReleaseControlPage = import.meta.env.DEV
    ? React.lazy(() => import('./components/ReleaseControlPage'))
    : null;
// Template-Kit: Katalog der Sektionsbausteine unter /template. Nur lokal.
// Anleitung: frontend/template/TEMPLATE.md
//
// 🔴 Die Bedingung gehoert um den `React.lazy`-Aufruf, nicht nur um die Route —
// genau wie oben bei `ReleaseControlPage`. Ein `React.lazy` auf oberster Ebene
// ist ein erreichbarer dynamischer Import, und Rollup baut den Chunk dann auch
// dann, wenn die Route im Produktionsbuild toter Code ist. Am 2026-08-09 genau
// so gemessen: `TemplateGallery-*.js` lag im `dist/`, obwohl `/template` dort
// nicht existiert — und mit ihm der Beams-Hero samt three.js.
const TemplateGalleryPage = import.meta.env.DEV
    ? React.lazy(() => import('./template/gallery/TemplateGallery'))
    : null;
import { brandConfig } from './config/brand';
import { WaveformRig } from './components/WaveformRig';

// Dynamic landing page loader — replaces 1,050+ explicit Route definitions
// The route map and individual components are loaded on-demand
const LandingPageLoader = React.lazy(() => import('./routes/LandingPageLoader'));

// Loading fallback for lazy-loaded components
const PageLoader: React.FC = () => (
    <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#0a0a0a',
    }}>
        <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid rgba(255, 90, 10, 0.2)',
            borderTopColor: '#ff5a0a',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
        }} />
    </div>
);

// Branded, light loader used as the Suspense fallback for authenticated routes.
// The dark PageLoader above clashes hard with the white login screen — flashing
// black in the middle of the login → dashboard hand-off makes the app feel slow
// and broken. This matches Auth's success screen and Dashboard's own "Loading
// your workspace…" state so the transition stays visually continuous. With the
// Dashboard chunk prefetched on the login page, this usually doesn't even show.
const DashboardLoader: React.FC = () => (
    <div style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f3f4f6',
        gap: '24px',
    }}>
        <WaveformRig />
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
                width: '40px', height: '40px', background: '#111',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)',
            }}>
                <span style={{ fontWeight: 900, color: brandConfig.primaryColor, fontSize: '18px' }}>{brandConfig.logoLetter}</span>
            </div>
            <span style={{ fontSize: '20px', fontWeight: 900, letterSpacing: '-0.05em', color: '#111827' }}>{brandConfig.name}</span>
        </div>
        <div style={{
            fontSize: '10px', fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: '0.2em', color: '#9ca3af',
        }}>
            Loading your workspace&hellip;
        </div>
    </div>
);

interface ErrorBoundaryProps {
    children: ReactNode;
}

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    props: ErrorBoundaryProps;
    state: ErrorBoundaryState = { hasError: false, error: null };

    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.props = props;
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        const errorMessage = error.message || (typeof error === 'string' ? error : '');
        const isChunkLoadError = 
            error.name === 'ChunkLoadError' ||
            errorMessage.includes('Failed to fetch dynamically imported module') ||
            errorMessage.includes('dynamically imported module') ||
            errorMessage.includes('Loading chunk');
            
        // Don't render the error boundary UI for chunk load errors, let componentDidCatch reload it
        if (isChunkLoadError || isKnownHarmlessBrowserDomError(error)) {
            return { hasError: false, error: null };
        }
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        const errorMessage = error.message || (typeof error === 'string' ? error : '');
        const isChunkLoadError = 
            error.name === 'ChunkLoadError' ||
            errorMessage.includes('Failed to fetch dynamically imported module') ||
            errorMessage.includes('dynamically imported module') ||
            errorMessage.includes('Loading chunk');

        if (isKnownHarmlessBrowserDomError(error)) {
            return;
        }

        if (isChunkLoadError) {
            // Prevent infinite reload loops 
            const reloadCount = parseInt(sessionStorage.getItem('chunk_reload_count') || '0', 10);
            if (reloadCount < 2) {
                sessionStorage.setItem('chunk_reload_count', (reloadCount + 1).toString());
                console.warn('Chunk load error detected, bypassing cache to fetch latest version...');
                // Bust cache by appending a unique timestamp to the URL query string
                const newUrl = new URL(window.location.href);
                newUrl.searchParams.set('v', Date.now().toString());
                window.location.replace(newUrl.toString());
                return;
            }
        }

        // Report to Sentry if not a simple chunk reload issue
        sentryCaptureException(error, {
            context: { source: 'ErrorBoundary' },
            extra: { componentStack: errorInfo.componentStack }
        });
    }

    componentDidMount() {
        const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
            const reason = event.reason;
            const message = reason?.message || (typeof reason === 'string' ? reason : '');
            
            const isChunkLoadError = 
                reason?.name === 'ChunkLoadError' ||
                message.includes('Failed to fetch dynamically imported module') ||
                message.includes('dynamically imported module') ||
                message.includes('Loading chunk');

            if (isChunkLoadError) {
                const reloadCount = parseInt(sessionStorage.getItem('chunk_reload_count') || '0', 10);
                if (reloadCount < 2) {
                    sessionStorage.setItem('chunk_reload_count', (reloadCount + 1).toString());
                    console.warn('Unhandled chunk load error detected, bypassing cache...');
                    const newUrl = new URL(window.location.href);
                    newUrl.searchParams.set('v', Date.now().toString());
                    window.location.replace(newUrl.toString());
                    return;
                }
            }

            sentryCaptureException(event.reason, { context: { source: 'ErrorBoundary' } });
        };
        window.addEventListener('unhandledrejection', handleUnhandledRejection);
        (this as any)._unhandledRejectionHandler = handleUnhandledRejection;
    }

    componentWillUnmount() {
        if ((this as any)._unhandledRejectionHandler) {
            window.removeEventListener('unhandledrejection', (this as any)._unhandledRejectionHandler);
        }
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen flex items-center justify-center bg-gray-50">
                    <div className="text-center p-8">
                        <h1 className="text-2xl font-bold text-red-600 mb-4">Something went wrong</h1>
                        <p className="text-gray-600 mb-4">{this.state.error?.message}</p>
                        <button
                            onClick={() => window.location.href = '/login'}
                            className="bg-[#ff5a0a] text-white px-6 py-2 rounded font-bold"
                        >
                            Go to Login
                        </button>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

/** Captures /ref/:code and /ref/u/:code → redirects to /signup?ref=:code */
const ReferralRedirect: React.FC = () => {
    const { code } = useParams();
    return <Navigate to={`/signup?ref=${code || ''}`} replace />;
};

const ProtectedRoute: React.FC<{ children: React.ReactNode; adminOnly?: boolean }> = ({ children, adminOnly }) => {
    const user = db.getCurrentUser();

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    if (adminOnly && user.role !== 'admin') {
        return <Navigate to="/dashboard" replace />;
    }

    return <>{children}</>;
};

const LegalWrapper: React.FC = () => {
    const { type } = useParams<{ type: string }>();
    const navigate = useNavigate();

    const handleBack = () => {
        const user = db.getCurrentUser();
        if (user && (user.role === 'user' || user.role === 'admin')) {
            navigate('/dashboard');
        } else {
            navigate('/');
        }
    };

    return <Legal type={type as any} onBack={handleBack} />;
};

const App: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const uiReleaseMode = useUiReleaseMode();
    const [chatWidgetReady, setChatWidgetReady] = useState(false);
    const chatWidgetLoadedRef = useRef(false);
    const isLocalDesignPreview = import.meta.env.DEV
        && (location.pathname === '/landing-redesign-preview'
            || location.pathname.startsWith('/admin-redesign-preview')
            || location.pathname === '/release-control');

    useEffect(() => {
        document.documentElement.dataset.uiRelease = uiReleaseMode;
        if (window.gtag) {
            window.gtag('set', { ui_release: uiReleaseMode });
        }
    }, [uiReleaseMode]);

    // Initialize DB on mount
    useEffect(() => {
        if (isLocalDesignPreview) {
            return;
        }

        db.init();

        // Capture Google Ads Click ID (GCLID) for Value-Based Bidding
        const urlParams = new URLSearchParams(window.location.search);
        const clickId = urlParams.get('gclid');
        if (clickId) {
            localStorage.setItem('tgp_gclid', clickId);
        }
    }, [isLocalDesignPreview]);

    // Sync Sentry user context with auth state
    useEffect(() => {
        if (isLocalDesignPreview) {
            clearSentryUser();
            return;
        }

        const user = db.getCurrentUser();
        if (user) {
            setSentryUser({ id: user.id, email: user.email, role: user.role });
        } else {
            clearSentryUser();
        }
    }, [location, isLocalDesignPreview]);

    // Track Presence based on location
    useEffect(() => {
        if (isLocalDesignPreview) {
            return;
        }

        const user = db.getCurrentUser();
        if (user) {
            // Map path to "view" for legacy support in presence tracking
            const path = location.pathname.substring(1) || 'landing';
            db.trackPresence(path, user);

            // Set GA4 User-ID for cross-session/cross-device tracking
            if (window.gtag) {
                window.gtag('set', { user_id: user.id });
            }
        }
    }, [location, isLocalDesignPreview]);

    // Track SPA page views in GA4 on every route change
    useEffect(() => {
        if (isLocalDesignPreview) {
            return;
        }
        trackPageView(location.pathname + location.search);
    }, [location, isLocalDesignPreview]);

    useEffect(() => {
        if (isLocalDesignPreview) return;

        const handleAuthExpired = () => {
            if (location.pathname === '/login') return;
            clearSentryUser();
            if (window.gtag) {
                window.gtag('set', { user_id: null });
            }
            const returnPath = `${location.pathname}${location.search}`;
            navigate(
                `/login?reason=session-expired&return=${encodeURIComponent(returnPath)}`,
                { replace: true },
            );
        };

        window.addEventListener('auth-expired', handleAuthExpired);
        return () => window.removeEventListener('auth-expired', handleAuthExpired);
    }, [isLocalDesignPreview, location.pathname, location.search, navigate]);

    // Keep route ownership with React Router. Calling navigate() from a global
    // Navigation API interceptor starts a second navigation inside the first in
    // Chromium, which skips the active View Transition and emits AbortError
    // noise. The routed Suspense fallbacks already provide continuous feedback.
    const navigateToRoot = () => {
        navigate('/');
        window.scrollTo(0, 0);
    };

    // ── Chunk-Reload-Counter reset ──
    // The ErrorBoundary bumps sessionStorage['chunk_reload_count'] on every
    // ChunkLoadError and gives up after 2 reloads. Without a reset, a user who
    // hits 2 chunk failures early in a session gets dead clicks forever
    // (subsequent lazy loads silently fail). Reset on every successful route
    // change so the counter only tracks *consecutive* failures.
    useEffect(() => {
        sessionStorage.removeItem('chunk_reload_count');
    }, [location.pathname]);

    // Keep public landing pages focused on the hero first. The lightweight
    // WhatsApp launcher is deferred until idle, while dashboard/support pages
    // keep it immediately available.
    useEffect(() => {
        if ((window as any).__PRERENDER__) {
            setChatWidgetReady(false);
            return;
        }

        if (isLocalDesignPreview) {
            setChatWidgetReady(false);
            return;
        }

        const loadImmediately =
            location.pathname.startsWith('/dashboard') ||
            location.pathname.startsWith('/admin') ||
            location.pathname.startsWith('/support/live');

        if (loadImmediately) {
            chatWidgetLoadedRef.current = true;
            setChatWidgetReady(true);
            return;
        }

        if (chatWidgetLoadedRef.current) {
            setChatWidgetReady(true);
            return;
        }

        setChatWidgetReady(false);
        let idleId: number | null = null;
        const showChatWidget = () => {
            chatWidgetLoadedRef.current = true;
            setChatWidgetReady(true);
        };
        const timeoutId = window.setTimeout(() => {
            if ('requestIdleCallback' in window) {
                idleId = window.requestIdleCallback(showChatWidget, { timeout: 2500 });
            } else {
                showChatWidget();
            }
        }, 3000);

        return () => {
            window.clearTimeout(timeoutId);
            if (idleId !== null && 'cancelIdleCallback' in window) {
                window.cancelIdleCallback(idleId);
            }
        };
    }, [location.pathname, isLocalDesignPreview]);

    const handleLogin = (role: 'user' | 'admin') => {
        // ⚠️ Plain navigate(): the destination
        // (Dashboard/AdminPanel) is React.lazy, so its render suspends while the
        // chunk loads. document.startViewTransition() snapshots the current page
        // (Auth's white success screen) and freezes it as an overlay until the
        // callback's DOM update settles — but navigate() returns before the lazy
        // chunk resolves, so the frozen success-screen screenshot can stay on top
        // indefinitely and the user never reaches the dashboard. The Suspense
        // fallback (branded loader) handles the chunk-load wait correctly instead.
        navigate(role === 'admin' ? '/admin' : '/dashboard');
        window.scrollTo(0, 0);
    };

    const handleLogout = () => {
        // Clear GA4 User-ID on logout
        if (window.gtag) {
            window.gtag('set', { user_id: null });
        }
        db.logout();
    };

    const handleBack = () => {
        navigateToRoot();
    };

    return (
        <ErrorBoundary>
            <RouteSeoPolicy />
            <ThemeRoutePolicy />
            <Suspense fallback={<PageLoader />}>
                <Routes>
                    <Route path="/" element={
                        brandConfig.id === 'trafficcreator' 
                        ? shouldUseRedesignForSurface(uiReleaseMode, 'homepage')
                            ? <LandingRedesignPage />
                            : <OptimizedLandingPage onLogin={() => navigate('/login')} onNavigate={(page) => navigate(`/${page}`)} />
                        : <MetadeckLanding onLogin={() => navigate('/login')} onNavigate={(page) => navigate(`/${page}`)} />
                    } />

                    {/* Design previews are local-only and never ship as public bypass routes. */}
                    {import.meta.env.DEV && (
                        <>
                            <Route path="/metadeck-preview" element={
                                <MetadeckLanding onLogin={() => navigate('/login')} onNavigate={(page) => navigate(`/${page}`)} />
                            } />
                            <Route path="/landing-new" element={
                                <OptimizedLandingPage onLogin={() => navigate('/login')} onNavigate={(page) => navigate(`/${page}`)} />
                            } />
                            {brandConfig.id === 'trafficcreator' && (
                                <>
                                    <Route path="/landing-redesign-preview" element={<LandingRedesignPage previewMode />} />
                                    <Route path="/landing-v3" element={<LandingRedesignV3Page />} />
                                    {TemplateGalleryPage && (
                                        <>
                                            <Route path="/template" element={<TemplateGalleryPage />} />
                                            {/* Einzelne Variante bildschirmfuellend — so laesst sich das
                                                Verhalten bei anderen Breiten am echten Viewport pruefen. */}
                                            <Route path="/template/:variantId" element={<TemplateGalleryPage />} />
                                        </>
                                    )}
                                </>
                            )}
                            {/* Splat: AdminPanel mounts its own nested <Routes> for every admin page. */}
                            <Route path="/admin-redesign-preview/*" element={<AdminRedesignPage />} />
                            <Route path="/youtube-smm-preview" element={<YouTubeSmmPreview />} />
                            <Route path="/youtube-preview" element={
                                <YouTubeLanding onLogin={() => navigate('/login')} onNavigate={(page) => navigate(`/${page}`)} />
                            } />
                        </>
                    )}

                    {ReleaseControlPage && (
                        <Route path="/release-control" element={<ReleaseControlPage />} />
                    )}

                    <Route path="/youtube" element={
                        <PublicSpecialReleaseSurface mode={brandConfig.id === 'trafficcreator' ? uiReleaseMode : 'legacy'} surface="youtube">
                            <YouTubeLanding onLogin={() => navigate('/login')} onNavigate={(page) => navigate(`/${page}`)} />
                        </PublicSpecialReleaseSurface>
                    } />

                    {brandConfig.id === 'trafficcreator' && (
                        <Route path="/pricing" element={
                            <PublicSpecialReleaseSurface mode={uiReleaseMode} surface="pricing">
                                <PricingPage />
                            </PublicSpecialReleaseSurface>
                        } />
                    )}

                    {/* Auth Routes */}
                    <Route path="/login" element={<Auth view="login" onLogin={handleLogin} onNavigate={(v) => navigate(`/${v}`)} />} />
                    <Route path="/signup" element={<Auth view="signup" onLogin={handleLogin} onNavigate={(v) => navigate(`/${v}`)} />} />
                    <Route path="/forgot" element={<Auth view="forgot" onLogin={handleLogin} onNavigate={(v) => navigate(`/${v}`)} />} />
                    <Route path="/verify-email" element={<VerifyEmail />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/sso" element={<SSOHandler />} />

                    {/* Referral link redirects → signup with ref code */}
                    <Route path="/ref/:code" element={<ReferralRedirect />} />
                    <Route path="/ref/u/:code" element={<ReferralRedirect />} />
                    <Route path="/register" element={<Navigate to={`/signup${window.location.search}`} replace />} />
                    <Route path="/support/live/:ticketId" element={<PublicChatReply />} />


                    {/* Protected Routes — wrapped in their own Suspense so the
                        lazy Dashboard/Admin chunk shows the light, branded loader
                        instead of the dark app-level PageLoader (no black flash
                        right after the white login screen). */}
                    <Route path="/dashboard/*" element={
                        <ProtectedRoute>
                            <Suspense fallback={<DashboardLoader />}>
                                <Dashboard onLogout={handleLogout} onNavigate={(page) => navigate(`/${page}`)} />
                            </Suspense>
                        </ProtectedRoute>
                    } />

                    <Route path="/admin/*" element={
                        <ProtectedRoute adminOnly>
                            <Suspense fallback={<DashboardLoader />}>
                                <AdminPanel onLogout={handleLogout} />
                            </Suspense>
                        </ProtectedRoute>
                    } />

                    {/* Other Pages */}
                    {brandConfig.id === 'trafficcreator' && (
                        <>
                            <Route path="/helpdesk" element={<HelpDesk onBack={handleBack} />} />
                            <Route path="/helpdesk/:category" element={<HelpDesk onBack={handleBack} />} />
                            <Route path="/helpdesk/:category/:slug" element={<HelpDesk onBack={handleBack} />} />
                            <Route path="/blog" element={<Blog onBack={handleBack} />} />
                            <Route path="/blog/:slug" element={<Blog onBack={handleBack} />} />
                            <Route path="/:lang/blog" element={<Blog onBack={handleBack} />} />
                            <Route path="/:lang/blog/:slug" element={<Blog onBack={handleBack} />} />
                            
                            {/* Legal Routes */}
                            <Route path="/legal/:type" element={<LegalWrapper />} />
                            <Route path="/privacy" element={<Navigate to="/legal/privacy" replace />} />
                            <Route path="/terms" element={<Navigate to="/legal/terms" replace />} />
                            <Route path="/impressum" element={<Navigate to="/legal/impressum" replace />} />
                            <Route path="/agb" element={<Navigate to="/legal/agb" replace />} />
                            <Route path="/refund" element={<Navigate to="/legal/refund" replace />} />
                            <Route path="/delivery" element={<Navigate to="/legal/delivery" replace />} />
                            <Route path="/account-deletion" element={<AccountDeletionPage />} />

                            {/* About Page */}
                            <Route path="/about" element={<AboutPage onBack={handleBack} />} />

                            {import.meta.env.DEV && (
                                <Route path="/pricing-sample" element={
                                    <React.Suspense fallback={<PageLoader />}>
                                        {React.createElement(React.lazy(() => import('./components/landing/SamplePricing')))}
                                    </React.Suspense>
                                } />
                            )}
                        </>
                    )}

                    {/* 
                     * Dynamic Landing Pages — replaces 1,050+ explicit route definitions.
                     * The LandingPageLoader dynamically imports the route map and resolves
                     * the correct landing page component based on the current URL pathname.
                     * This moves ~330KB of route declarations out of the main bundle.
                     */}
                    { brandConfig.id === 'trafficcreator' && <Route path="*" element={<LandingPageLoader />} /> }
                    { brandConfig.id === 'metadeck' && <Route path="*" element={<Navigate to="/" replace />} /> }
                </Routes>
            </Suspense>
            {chatWidgetReady && !isLocalDesignPreview && (
                <Suspense fallback={null}>
                    <ChatWidget />
                </Suspense>
            )}
        </ErrorBoundary>
    );
};

export default App;
